import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@src/components/ui/form";
import { Input } from "@src/components/ui/input";
import { DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX } from "@src/helpers/regex";
import useZustandStore from "@src/helpers/zustand";
import { usePepConnectionTypes } from "@src/api/usePepConnectionTypes";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/components/ui/select";
import { WITH_PAYUNICARD } from "@src/utils/consts";
import { useSaveUser } from "@src/components/screens/PEP/use-save-user";
import { Loader } from "lucide-react";

const FormSchema = z.object({
  pepName: z.string().refine((v) => v),
  pepSurname: z.string().refine((v) => v),
  pepWorkingPosition: z.string().refine((v) => v),
  pepRelation: z.string().refine((v) => v),
});

function PepDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);
  const pepConnectionTypes = usePepConnectionTypes();
  const isRedirectedFromPay = location.search.includes(
    "redirected_from=identify-with-payunicard"
  );
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      pepName: zData.pep.pepName,
      pepSurname: zData.pep.pepSurname,
      pepWorkingPosition: zData.pep.pepWorkingPosition,
      pepRelation: zData.pep.pepRelation,
    },
  });

  const { onSaveUser, loading } = useSaveUser(() => {
    if (WITH_PAYUNICARD && isRedirectedFromPay) {
      navigate(
        `/${i18n.language}/almost-done?redirected_from=identify-with-payunicard`
      );
    } else {
      navigate(`/${i18n.language}/almost-done`);
    }
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    const newData = {
      ...zData,
      pep: {
        ...zData.pep,
        pepName: data.pepName,
        pepSurname: data.pepSurname,
        pepWorkingPosition: data.pepWorkingPosition,
        pepRelation: data.pepRelation,
      },
    };
    setData(newData);
    onSaveUser(newData);
  }

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc title={t("pep.details.title")} />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          {/* name */}
          <FormField
            control={form.control}
            name="pepName"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t("pep.inputs.pepName")}
                      onChange={(e) => {
                        const { target } = e;
                        if (
                          DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                            target.value
                          ) ||
                          !target.value
                        ) {
                          field.onChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          {/* Surname */}
          <FormField
            control={form.control}
            name="pepSurname"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t("pep.inputs.pepSurname")}
                      onChange={(e) => {
                        const { target } = e;
                        if (
                          DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                            target.value
                          ) ||
                          !target.value
                        ) {
                          field.onChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />
          <FormField
            control={form.control}
            name="pepWorkingPosition"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={t("pep.inputs.pepWorkingPosition")}
                      onChange={(e) => {
                        const { target } = e;
                        if (
                          DIGIT_SPACE_DASH_LATIN_GEORGIAN_ONLY_REGEX.test(
                            target.value
                          ) ||
                          !target.value
                        ) {
                          field.onChange(e);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="pepRelation"
            render={({ field }) => {
              return (
                <Select onValueChange={field.onChange} value={field.value}>
                  <SelectTrigger
                    className="w-full text-base"
                    placeholder={t("pep.inputs.pepRelation")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pepConnectionTypes.data?.map((el) => (
                      <SelectItem key={el.type} value={el.type}>
                        {el.displayText}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              );
            }}
          />

          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full relative"
                disabled={!form.formState.isValid || loading}
                type="submit"
              >
                {loading ? (
                  <div className="absolute inset-0 justify-center flex items-center w-full">
                    <Loader className="animate-spin" />
                  </div>
                ) : (
                  t("continue")
                )}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default PepDetails;
